import type { ServerResponse } from "@/http"
import type { IAlbumResponse } from "@/types"
import { useMutation } from "@tanstack/vue-query"
import { axios } from "@/axios"
import { Album } from "@/models"

export async function updateAlbum(eventVaultId: number, album: Album): Promise<Album> {
  if (!album.id) {
    throw new Error("Album must have an id.")
  }

  const albumFields = new FormData()

  albumFields.append("title", album.title?.trim() ?? "")
  if (album.description)
    albumFields.append("description", album.description)
  if (album.date)
    albumFields.append("albumDate", album.date?.toDateString())
  if (album.coverImage)
    albumFields.append("coverImageFile", album.coverImage)
  if (album.coverImageUrl)
    albumFields.append("coverImageURL", album.coverImageUrl)
  if (album.passcode)
    albumFields.append("albumPasscode", album.passcode)
  if (album.isLocked)
    albumFields.append("isLocked", album.isLocked!.toString())
  if (album.isHidden)
    albumFields.append("isHidden", album.isHidden!.toString())
  if (album.location && album.location?.formattedAddress)
    albumFields.append("location", JSON.stringify(album.location))

  if ("disableUploads" in album.settings) {
    albumFields.append("AlbumSettings.DisableGuestUpload", (album.settings?.disableUploads ?? false).toString())
  }

  if ("sharePix" in album.settings) {
    albumFields.append("AlbumSettings.SharePixWithGuests", (album.settings?.sharePix ?? false).toString())
  }

  const response = await axios.put<ServerResponse<IAlbumResponse>>(
    `api/event/${eventVaultId}/album/${album.id}`,
    albumFields,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  )

  return Album.fromResponse(response.data.Data)
}

export function useUpdateAlbum() {
  return useMutation({
    mutationFn: ({ eventVaultId, album }: { eventVaultId: number, album: Album }) => updateAlbum(eventVaultId, album),
  })
}
